import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import React from 'react';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog';
import { AritoIcon } from '@/components/custom/arito/icon';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ConfirmationDialogV2Props {
  open?: boolean;
  onClose?: () => void;
  onCloseConfirmDialog?: () => void;
  onConfirm?: () => void | Promise<void>;
  onDelete?: (uuid: string) => Promise<any>;
  clearSelection?: () => void;
  title?: string;
  message?: React.ReactNode;
  content?: React.ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  iconCode?: number;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  disableBackdropClose?: boolean;
  disableEscapeKeyDown?: boolean;
  selectedObj?: any | null;
  showCancelBtn?: boolean;
}

export const ConfirmationDialogV2: React.FC<ConfirmationDialogV2Props> = ({
  open = false,
  onClose,
  onCloseConfirmDialog,
  onConfirm,
  onDelete,
  clearSelection,
  title = 'Cảnh báo',
  message = 'Bạn có chắc chắn không?',
  content,
  confirmButtonText = 'Đồng ý',
  cancelButtonText = 'Hủy',
  iconCode = 260,
  maxWidth = 'sm',
  disableBackdropClose = false,
  disableEscapeKeyDown = false,
  selectedObj,
  showCancelBtn = true
}) => {
  const [isConfirming, setIsConfirming] = React.useState(false);
  const widthClasses = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl'
  };
  const handleClose = React.useCallback(() => {
    onClose?.();
    onCloseConfirmDialog?.();
  }, [onClose, onCloseConfirmDialog]);

  const handleConfirm = React.useCallback(async () => {
    setIsConfirming(true);
    try {
      if (onDelete && selectedObj?.uuid) {
        await onDelete(selectedObj.uuid);
        clearSelection?.();
      }
      await onConfirm?.();
    } finally {
      setIsConfirming(false);
    }
  }, [clearSelection, onConfirm, onDelete, selectedObj?.uuid]);

  return (
    <Dialog
      open={open}
      onOpenChange={val => {
        if (!val) handleClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay className='fixed inset-0 z-[9999] bg-black/50' />
        <div className='fixed inset-0 z-[9999] flex items-center justify-center p-4'>
          <DialogPrimitive.Content
            onEscapeKeyDown={e => {
              if (disableEscapeKeyDown) e.preventDefault();
            }}
            onPointerDownOutside={e => {
              if (disableBackdropClose) e.preventDefault();
            }}
            className={cn(
              'flex w-full flex-col overflow-hidden rounded border border-[#cfd8e3] bg-white shadow-lg outline-none',
              widthClasses[maxWidth] || 'max-w-md'
            )}
          >
            {/* Header */}
            <div className='flex h-[var(--erp-dialog-header-height)] min-h-[var(--erp-dialog-header-height)] items-center justify-between border-b border-[#d1d5db] bg-[#f2f7fc] pl-3.5 pr-4'>
              <DialogTitle className='flex items-center text-[length:var(--erp-dialog-title-size)] font-bold text-black'>
                <AritoIcon icon={iconCode} className='mx-2' />
                <span>{title}</span>
              </DialogTitle>
              <button
                type='button'
                onClick={handleClose}
                className='inline-flex size-8 items-center justify-center text-black/80 hover:text-black'
                title='Đóng'
              >
                <Lineicons aria-hidden='true' icon={XmarkStroke} className='size-[18px]' />
              </button>
            </div>

            {/* Content */}
            <div className='px-4 py-2.5 text-base font-medium text-gray-800'>{content ?? message}</div>

            {/* Footer / Actions */}
            <div className='flex justify-end gap-2 border-t border-[#e2e8f0] bg-[#f8fafc] p-3'>
              <Button
                className='flex h-9 items-center gap-1.5 rounded-sm bg-main px-4 font-bold text-main-foreground hover:bg-main/80'
                onClick={() => void handleConfirm()}
                disabled={isConfirming}
              >
                <AritoIcon icon={884} className='text-white' />
                {isConfirming && onDelete ? 'Đang xóa...' : confirmButtonText}
              </Button>
              {showCancelBtn && (
                <Button
                  variant='outline'
                  className='flex h-9 items-center gap-1.5 rounded-sm border border-gray-300 px-4 font-semibold text-gray-700 hover:bg-gray-50'
                  onClick={handleClose}
                  disabled={isConfirming}
                >
                  <AritoIcon icon={885} />
                  {cancelButtonText}
                </Button>
              )}
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
};
export default ConfirmationDialogV2;
